import { useEffect, useRef, useState } from 'react';

// Termy, alive: the eyes follow `target` (a screen point — e.g. the form field
// the user just focused) and the raised hand waves whenever `waveSignal` changes.
export default function Mascot({ target = null, waveSignal = 0, className = '' }) {
  const ref = useRef(null);
  const [pupil, setPupil] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!target) { setPupil({ x: 0, y: 0 }); return; }
    const r = el.getBoundingClientRect();
    const eyeX = r.left + r.width / 2;
    const eyeY = r.top + r.height * 0.52;       // eyes sit a touch above centre
    const dx = target.x - eyeX;
    const dy = target.y - eyeY;
    const dist = Math.hypot(dx, dy) || 1;
    const max = 2.6;                            // keeps pupils inside the whites
    setPupil({ x: (dx / dist) * max, y: (dy / dist) * max });
  }, [target]);

  return (
    <svg ref={ref} viewBox="0 0 128 128" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="m-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#26262e" /><stop offset="1" stopColor="#1a1a20" />
        </linearGradient>
        <linearGradient id="m-screen" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2c2c34" /><stop offset="1" stopColor="#23232b" />
        </linearGradient>
      </defs>

      <ellipse cx="64" cy="116" rx="34" ry="5" fill="#1a1a20" opacity="0.12" />

      {/* legs */}
      <rect x="46" y="100" width="9" height="13" rx="4.5" fill="#1a1a20" stroke="#3a3a44" strokeWidth="1.2" />
      <rect x="73" y="100" width="9" height="13" rx="4.5" fill="#1a1a20" stroke="#3a3a44" strokeWidth="1.2" />

      {/* right arm holding a ticket */}
      <path d="M104 72 q9 4 11 11" fill="none" stroke="#3a3a44" strokeWidth="8.6" strokeLinecap="round" />
      <path d="M104 72 q9 4 11 11" fill="none" stroke="#1a1a20" strokeWidth="7" strokeLinecap="round" />
      <g transform="rotate(16 112 87)">
        <rect x="100" y="79" width="24" height="16" rx="3" fill="#f97316" />
        <rect x="100" y="79" width="24" height="16" rx="3" fill="none" stroke="#ea580c" strokeWidth="1.5" />
        <line x1="111" y1="80" x2="111" y2="94" stroke="#ea580c" strokeWidth="1.5" strokeDasharray="2.3 2.3" />
      </g>

      {/* terminal body */}
      <rect x="18" y="26" width="92" height="76" rx="16" fill="url(#m-body)" />
      <rect x="18.75" y="26.75" width="90.5" height="74.5" rx="15.25" fill="none" stroke="#3a3a44" strokeWidth="1.5" />

      {/* traffic lights */}
      <circle cx="33" cy="40" r="4.2" fill="#f87171" />
      <circle cx="46" cy="40" r="4.2" fill="#fbbf24" />
      <circle cx="59" cy="40" r="4.2" fill="#34d399" />

      {/* screen */}
      <rect x="27" y="50" width="74" height="44" rx="9" fill="url(#m-screen)" />

      {/* eyes — whites fixed, pupils track the target */}
      <circle cx="50" cy="66" r="5.2" fill="#e8e8ea" />
      <circle cx="78" cy="66" r="5.2" fill="#e8e8ea" />
      <g transform={`translate(${pupil.x} ${pupil.y})`} style={{ transition: 'transform 0.16s ease-out' }}>
        <circle cx="50" cy="66" r="2" fill="#1a1a20" />
        <circle cx="78" cy="66" r="2" fill="#1a1a20" />
      </g>

      {/* smile + orange prompt + blinking cursor */}
      <path d="M52 78 q12 10 24 0" fill="none" stroke="#e8e8ea" strokeWidth="3.2" strokeLinecap="round" />
      <path d="M35 72 l5 4 -5 4" fill="none" stroke="#f97316" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="86" y="73" width="7" height="9" rx="1.5" fill="#f97316" />

      {/* raised hand that waves (re-keyed by waveSignal to replay) */}
      <g key={waveSignal} className="ticky-wave-arm">
        <path d="M22 74 Q12 62 10 50" fill="none" stroke="#3a3a44" strokeWidth="8.6" strokeLinecap="round" />
        <path d="M22 74 Q12 62 10 50" fill="none" stroke="#1a1a20" strokeWidth="7" strokeLinecap="round" />
        <circle cx="10" cy="48" r="5.5" fill="#1a1a20" stroke="#3a3a44" strokeWidth="1.2" />
      </g>
    </svg>
  );
}
